"use client"

import { useState, useEffect } from 'react'
import { ArrowLeft, PlusIcon, Download, CalendarIcon, Loader2, ArrowUpDown, MoreHorizontal, Trash, Edit2, FileText, Filter, RefreshCw } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import * as XLSX from 'xlsx';
import { DataTable } from "../../components/data-table"
import { columns, InvoiceType } from './columns'
import InvoiceService from '../../api/invoices.service'
import DataRangeService from '../../api/getdatainrange.service'
import { Separator } from "@/components/ui/separator"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"

async function getData(setCanLoadMore: any, skip: number, businessId: number): Promise<InvoiceType[]> {
  const response = await InvoiceService.getAllInvoices(businessId, skip);
  if (response.data.result === 'invalid') {
    //window.location.href = '/logout';
    return [];
  }
  // Notify if exactly 50 invoices are returned
  if (response.data.invoices.length === 50) {
    setCanLoadMore(true); // No more data to load
  }
  return response.data.invoices.map((invoice: any) => ({
    id: invoice.id,
    publicId: invoice.publicId,
    invoiceNumber: invoice.invoiceNumber,
    amount: invoice.amount,
    currency: invoice.currency,
    status: invoice.status,
    issueDate: invoice.issueDate,
    dueDate: invoice.dueDate,
    dateUpdated: invoice.dateUpdated,
    dateCreated: invoice.dateCreated,
    receipts: invoice.receipts,
  }));
}

async function getDataByDateRange(startDate: Date, endDate: Date, businessId: number): Promise<InvoiceType[]> {
  const startYear = startDate.getFullYear();
  const startMonth = startDate.getMonth() + 1; // Months are zero-based
  const startDay = startDate.getDate();
  const endYear = endDate.getFullYear();
  const endMonth = endDate.getMonth() + 1;
  const endDay = endDate.getDate();

  const response = await DataRangeService.getDataUsingDateRangeWithSectionId('invoices', businessId, startYear, startMonth, startDay, endYear, endMonth, endDay);
  if (response.data.result === 'invalid') {
    window.location.href = '/logout';
    return [];
  }
  return response.data.invoices.map((invoice: any) => ({
    id: invoice.id,
    publicId: invoice.publicId,
    invoiceNumber: invoice.invoiceNumber,
    amount: invoice.amount,
    currency: invoice.currency,
    status: invoice.status,
    issueDate: invoice.issueDate,
    dueDate: invoice.dueDate,
    dateUpdated: invoice.dateUpdated,
    dateCreated: invoice.dateCreated,
    receipts: invoice.receipts,
  }));
}

export default function Invoices() {
  const [data, setData] = useState<InvoiceType[]>([]);
  const [filteredData, setFilteredData] = useState<InvoiceType[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Add skip state for pagination
  const [skip, setSkip] = useState(0);
  const [canLoadMore, setCanLoadMore] = useState(false);

  // Date range states
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [isDateSearching, setIsDateSearching] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [activeTab, setActiveTab] = useState("all");

  const [selectedBusiness] = useState(() => {
    const storedBusiness = typeof window !== 'undefined' ? window.localStorage.getItem('selectedBusiness') : null;
    return storedBusiness ? JSON.parse(storedBusiness) : null;
  });

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      const businessId = selectedBusiness.id;
      const result = await getData(setCanLoadMore, skip, businessId);
      setIsLoading(false);
      setData(result);
      setFilteredData(result);
    };
    fetchData();
  }, [skip]);

  const handleDateRangeSearch = async () => {
    if (startDate && endDate) {
      setIsDateSearching(true);
      setIsLoading(true);
      const businessId = selectedBusiness.id;
      const result = await getDataByDateRange(startDate, endDate, businessId);
      setIsLoading(false);
      setIsDateSearching(false);
      setData(result);
    }
  };

  const resetDateFilter = async () => {
    setStartDate(null);
    setEndDate(null);
    setIsLoading(true);
    const businessId = selectedBusiness.id;
    const result = await getData(setCanLoadMore, 0, businessId);
    setIsLoading(false);
    setData(result);
    setFilteredData(result);
  };

  const exportToExcel = () => {
    setIsExporting(true);
    
    try {
      // Prepare data for export - exclude sensitive or unnecessary fields
      const exportData = filteredData.map((invoice: InvoiceType) => ({
        'Invoice Number': invoice.invoiceNumber,
        'Status': invoice.status,
        'Amount': invoice.amount,
        'Currency': invoice.currency,
        'Issue Date': invoice.issueDate,
        'Due Date': invoice.dueDate,
      }));
      
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Invoices");
      
      const currentDateTime = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      XLSX.writeFile(workbook, `Invoices_${currentDateTime}.xlsx`);
    } catch (error) {
      console.error("Export failed:", error);
    } finally {
      setIsExporting(false);
    }
  };

  // Calculate summary statistics
  const summaryStats = {
    total: data.length,
    paid: data.filter(invoice => invoice.status === 'paid').length,
    pending: data.filter(invoice => invoice.status === 'pending').length,
    overdue: data.filter(invoice => invoice.status === 'overdue').length,
    totalAmount: data.reduce((sum, invoice) => sum + invoice.amount, 0).toFixed(2),
    currency: data.length > 0 ? data[0].currency : 'USD',
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const filterByStatus = (status: string) => {
    setActiveTab(status);
    if (status === "all") {
      resetDateFilter();
    } else {
      setIsLoading(true);
      const filteredData = data.filter(invoice => 
        status === "all" || invoice.status.toLowerCase() === status.toLowerCase()
      );
      setFilteredData(filteredData);
      setIsLoading(false);
    }
  };

  return (
    <div className="container mx-auto py-8 space-y-8 px-4 sm:px-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-slate-700 to-slate-900 bg-clip-text text-transparent dark:from-slate-100 dark:to-slate-300">Invoices</h1>
          <p className="text-muted-foreground mt-1">Manage and track your business invoices</p>
        </div>
        
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => window.history.back()}
            className="border-slate-200 text-slate-600 hover:bg-slate-100 hover:text-slate-800 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-slate-100"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <Button 
            size="sm" 
            onClick={() => window.location.href = '/invoices/create'}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-md hover:shadow-lg transition-all duration-200"
          >
            <PlusIcon className="w-4 h-4 mr-2" /> Create Invoice
          </Button>
        </div>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <Card className="overflow-hidden border border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-md transition-all duration-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex flex-col">
                <span className="text-sm font-medium text-slate-500 dark:text-slate-400">Total Invoices</span>
                <span className="text-2xl font-bold mt-1">{summaryStats.total}</span>
              </div>
              <div className="h-12 w-12 bg-blue-50 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                <FileText className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="overflow-hidden border border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-md transition-all duration-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex flex-col">
                <span className="text-sm font-medium text-slate-500 dark:text-slate-400">Total Amount</span>
                <span className="text-2xl font-bold mt-1">{formatCurrency(parseFloat(summaryStats.totalAmount), summaryStats.currency)}</span>
              </div>
              <div className="h-12 w-12 bg-emerald-50 dark:bg-emerald-900/30 rounded-full flex items-center justify-center">
                <svg className="h-6 w-6 text-emerald-600 dark:text-emerald-400" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 6V18M18 12H6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="overflow-hidden border border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-md transition-all duration-200">
          <CardContent className="p-6">
            <div className="flex flex-col">
              <span className="text-sm font-medium text-slate-500 dark:text-slate-400">Status Breakdown</span>
              <div className="flex gap-2 mt-3">
                <Badge className="bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border-0 h-7 px-2.5 rounded-md whitespace-nowrap">
                  {summaryStats.paid} Paid
                </Badge>
                <Badge className="bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border-0 h-7 px-2.5 rounded-md whitespace-nowrap">
                  {summaryStats.pending} Pending
                </Badge>
                <Badge className="bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400 border-0 h-7 px-2.5 rounded-md whitespace-nowrap">
                  {summaryStats.overdue} Overdue
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="overflow-hidden border border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-md transition-all duration-200">
          <CardContent className="p-6">
            <div className="flex flex-col">
              <span className="text-sm font-medium text-slate-500 dark:text-slate-400">Quick Actions</span>
              <div className="flex gap-2 mt-3">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={exportToExcel} 
                  disabled={isExporting}
                  className="h-7 px-3 text-sm border-slate-200 text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                >
                  {isExporting ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> Exporting...
                    </>
                  ) : (
                    <>
                      <Download className="w-3.5 h-3.5 mr-2" /> Export
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      <Tabs defaultValue="all" value={activeTab} onValueChange={filterByStatus} className="w-full">
        <div className="flex items-center justify-between mb-4">
          <TabsList className="bg-slate-100 dark:bg-slate-800/50 p-1 rounded-lg">
            <TabsTrigger value="all" className="rounded-md data-[state=active]:bg-white dark:data-[state=active]:bg-slate-800 data-[state=active]:shadow-sm">
              All
            </TabsTrigger>
            <TabsTrigger value="paid" className="rounded-md data-[state=active]:bg-white dark:data-[state=active]:bg-slate-800 data-[state=active]:shadow-sm">
              Paid
            </TabsTrigger>
            <TabsTrigger value="pending" className="rounded-md data-[state=active]:bg-white dark:data-[state=active]:bg-slate-800 data-[state=active]:shadow-sm">
              Pending
            </TabsTrigger>
            <TabsTrigger value="overdue" className="rounded-md data-[state=active]:bg-white dark:data-[state=active]:bg-slate-800 data-[state=active]:shadow-sm">
              Overdue
            </TabsTrigger>
          </TabsList>
          
          <Button 
            variant="outline" 
            size="sm" 
            onClick={resetDateFilter}
            className="h-9 border-slate-200 text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            <RefreshCw className="w-4 h-4 mr-2" /> Reset Filters
          </Button>
        </div>
        
        <TabsContent value="all" className="mt-0">
          <Card className="border border-slate-200 dark:border-slate-800 shadow-sm">
            <CardHeader className="pb-3 border-b border-slate-200 dark:border-slate-800">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <CardTitle>Filter Invoices</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Use the search box below the table to find invoices by number
                </p>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex flex-col sm:flex-row gap-3 flex-1">
                  <div className="space-y-1 flex-1">
                    <label className="text-sm font-medium text-muted-foreground" htmlFor="startDate">From Date</label>
                    <div className="relative">
                      <CalendarIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input 
                        id="startDate" 
                        type="date" 
                        value={startDate ? startDate.toISOString().split('T')[0] : ''} 
                        onChange={(e) => setStartDate(e.target.value ? new Date(e.target.value) : null)} 
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div className="space-y-1 flex-1">
                    <label className="text-sm font-medium text-muted-foreground" htmlFor="endDate">To Date</label>
                    <div className="relative">
                      <CalendarIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input 
                        id="endDate" 
                        type="date" 
                        value={endDate ? endDate.toISOString().split('T')[0] : ''} 
                        onChange={(e) => setEndDate(e.target.value ? new Date(e.target.value) : null)} 
                        className="pl-10"
                      />
                    </div>
                  </div>
                </div>
                <div className="flex items-end space-x-2">
                  <Button 
                    variant="stripe" 
                    onClick={handleDateRangeSearch} 
                    isLoading={isDateSearching}
                    disabled={!startDate || !endDate}
                  >
                    Search
                  </Button>
                  {(startDate || endDate) && (
                    <Button variant="outline" onClick={resetDateFilter}>
                      Reset
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="paid" className="mt-0">
          <div className="bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-100 dark:border-emerald-900/20 rounded-lg p-4 mb-6">
            <p className="text-emerald-700 dark:text-emerald-400 text-sm">
              Showing {data.filter(invoice => invoice.status === 'paid').length} paid invoices with a total value of {formatCurrency(data.filter(invoice => invoice.status === 'paid').reduce((sum, invoice) => sum + invoice.amount, 0), summaryStats.currency)}
            </p>
          </div>
        </TabsContent>
        
        <TabsContent value="pending" className="mt-0">
          <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-900/20 rounded-lg p-4 mb-6">
            <p className="text-amber-700 dark:text-amber-400 text-sm">
              Showing {data.filter(invoice => invoice.status === 'pending').length} pending invoices with a total value of {formatCurrency(data.filter(invoice => invoice.status === 'pending').reduce((sum, invoice) => sum + invoice.amount, 0), summaryStats.currency)}
            </p>
          </div>
        </TabsContent>
        
        <TabsContent value="overdue" className="mt-0">
          <div className="bg-rose-50 dark:bg-rose-900/10 border border-rose-100 dark:border-rose-900/20 rounded-lg p-4 mb-6">
            <p className="text-rose-700 dark:text-rose-400 text-sm">
              Showing {data.filter(invoice => invoice.status === 'overdue').length} overdue invoices with a total value of {formatCurrency(data.filter(invoice => invoice.status === 'overdue').reduce((sum, invoice) => sum + invoice.amount, 0), summaryStats.currency)}
            </p>
          </div>
        </TabsContent>
      </Tabs>
      
      {canLoadMore && (
        <div className="flex justify-between items-center bg-slate-50 dark:bg-slate-800/50 rounded-lg p-3 border border-slate-200 dark:border-slate-800">
          <span className="text-sm text-slate-500 dark:text-slate-400">Showing records {skip + 1}-{skip + data.length}</span>
          <Button 
            variant="outline" 
            onClick={() => setSkip(skip + 50)} 
            disabled={isLoading}
            className="border-slate-200 text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading...
              </>
            ) : (
              <>
                Load More
              </>
            )}
          </Button>
        </div>
      )}
      
      <Card className="border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
        <DataTable columns={columns} data={filteredData} section="invoices" isLoading={isLoading} />
      </Card>
    </div>
  );
}
